/**
 * DemoExpiredModal (R60.13) — modal BLOCCANTE mostrato quando la demo è scaduta.
 *
 * Trigger: user.is_demo === true && demo_expires_at <= oggi.
 * Non è chiudibile: l'admin ha due strade obbligate:
 *   1. "Attiva il tuo piano ora" → naviga a /scegli-piano (pagamento Stripe)
 *   2. "Cancella account (GDPR)" → doppia conferma password + frase → hard delete
 *
 * Operator / non-admin vede solo il messaggio + logout (non può decidere).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertTriangle, CreditCard, Trash2, LogOut, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const REQUIRED_PHRASE = "CANCELLA IL MIO ACCOUNT";

export default function DemoExpiredModal({ user, daysOverdue }) {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [phrase, setPhrase] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isAdmin = user?.role === "admin";

  const goToPayment = () => {
    nav("/scegli-piano?src=demo-expired");
  };

  const doSelfDestroy = async () => {
    if (phrase.trim().toUpperCase() !== REQUIRED_PHRASE) {
      toast.error("Devi digitare esattamente: " + REQUIRED_PHRASE);
      return;
    }
    if (!password) {
      toast.error("Inserisci la password admin per confermare.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/orgs/me/self-destroy", {
        confirm_password: password,
        confirm_phrase: phrase.trim(),
      });
      toast.success("Account cancellato definitivamente. Arrivederci.", { duration: 3500 });
      setTimeout(() => {
        try { logout && logout(); } catch { /* noop */ }
        window.location.href = "/home";
      }, 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante la cancellazione");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={true}>
        <DialogContent
          className="max-w-md p-0 overflow-hidden gap-0 [&>button.absolute]:hidden"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          data-testid="demo-expired-modal"
        >
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 text-white px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest font-bold opacity-90">Demo terminata</p>
                <DialogTitle className="text-xl font-bold">La tua prova gratuita è scaduta</DialogTitle>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <DialogDescription className="text-slate-700 text-sm leading-relaxed">
              La demo di 7 giorni è terminata{daysOverdue > 0 && ` da ${daysOverdue} ${daysOverdue === 1 ? "giorno" : "giorni"}`}. Per continuare a usare e-Log HACCP devi attivare un piano annuale, oppure cancellare l&apos;account e i tuoi dati.
            </DialogDescription>

            {!isAdmin && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                <p className="text-xs text-slate-600">
                  Sei collegato come <b>operatore</b>. Solo l&apos;amministratore dell&apos;azienda può attivare il piano o cancellare l&apos;account.
                </p>
                <Button
                  variant="outline"
                  className="mt-3 w-full"
                  onClick={() => { try { logout && logout(); } catch { /* noop */ } window.location.href = "/login"; }}
                  data-testid="demo-expired-operator-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" /> Esci
                </Button>
              </div>
            )}

            {isAdmin && (
              <div className="space-y-3">
                <button
                  onClick={goToPayment}
                  className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-3.5 flex items-center justify-center gap-2 shadow-md active:scale-[.99] transition"
                  data-testid="demo-expired-cta-pay"
                >
                  <CreditCard className="w-5 h-5" /> Attiva il tuo piano ora
                </button>
                <p className="text-[11px] text-slate-500 text-center">
                  Piani annuali da €250 · pagamento sicuro Stripe · setup immediato
                </p>

                <div className="border-t border-slate-200 pt-3">
                  <button
                    onClick={() => setConfirmOpen(true)}
                    className="w-full rounded-xl bg-white border border-red-200 text-red-700 hover:bg-red-50 font-semibold px-4 py-2.5 flex items-center justify-center gap-2 transition text-sm"
                    data-testid="demo-expired-cta-destroy"
                  >
                    <Trash2 className="w-4 h-4" /> Cancella account (GDPR)
                  </button>
                  <p className="text-[11px] text-slate-500 text-center mt-2">
                    Diritto di cancellazione UE · elimina definitivamente tutti i tuoi dati
                  </p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Conferma cancellazione GDPR */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="demo-expired-destroy-dialog">
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <ShieldAlert className="w-5 h-5" /> Cancellazione definitiva
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-700">
            Questa operazione elimina in modo <b>irreversibile</b> l&apos;azienda, gli utenti e tutti i dati HACCP registrati. Riceverai un&apos;email di conferma GDPR.
          </DialogDescription>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Password amministratore</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="La tua password"
                data-testid="demo-expired-password"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Digita esattamente:</Label>
              <p className="text-sm font-mono font-bold text-red-700 my-1 select-none">{REQUIRED_PHRASE}</p>
              <Input
                value={phrase}
                onChange={(e) => setPhrase(e.target.value)}
                placeholder={REQUIRED_PHRASE}
                data-testid="demo-expired-phrase"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setConfirmOpen(false)}
              disabled={submitting}
              data-testid="demo-expired-destroy-cancel"
            >
              Annulla
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={doSelfDestroy}
              disabled={submitting || !password || phrase.trim().toUpperCase() !== REQUIRED_PHRASE}
              data-testid="demo-expired-destroy-confirm"
            >
              {submitting ? "Elimino…" : "Elimina tutto"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
